// A DEPLOY MUST NOT BREAK A FORM THAT IS ALREADY OPEN (TW-0622, 2026-11-04).
//
// The form is one bundle, `static/js/main.<hash>.js`, plus the pieces it loads on demand (the demo's QR code, qr.ts). Until
// that day `./run.sh deploy-form` synced with --delete and swept the previous build's files the moment the new ones landed,
// so a form open across a deploy that then asked for a piece got a 404. The deploy keeps every build's files for 14 days
// now, which is the real fix; this is the net under it: when one of our own scripts or stylesheets fails to load, or a
// lazy import rejects with a chunk error, the page reloads ONCE per address within 2 minutes (a sessionStorage stamp; storage
// that cannot be read reloads once anyway, held in memory). Nothing a real signing loads is lazy, so this cannot fire under a
// client halfway through their answers unless a file of ours really failed, and then the page was broken already.
//
// The same rule, word for word, as shared-components/stale-script-reload.ts, which the site and the shop app carry: this
// app is a separate 2023 CRA build that cannot import from outside src/, so it restates it, and stale-script-reload.test.ts
// holds it. Installed by index.tsx after the beacon's error handlers, as LISTENERS beside them, never replacing them.

export const STALE_SCRIPT_MESSAGES = [
  "ChunkLoadError",
  "Loading chunk",
  "Loading CSS chunk",
  "Failed to fetch dynamically imported module", // Chrome
  "Importing a module script failed", // Safari
  "error loading dynamically imported module", // Firefox
] as const;

export const RELOAD_WINDOW_MS = 2 * 60 * 1000;
export const STAMP_PREFIX = "wt-stale-script-reload:";
/** Where this app's build files live (create-react-app's static/js and static/css). */
export const FORM_ASSET_PREFIX = "/static/";

export function isStaleScriptError(err: unknown): boolean {
  if (err == null) return false;
  let text: string;
  if (typeof err === "string") text = err;
  else if (typeof err === "object") {
    const e = err as { name?: unknown; message?: unknown };
    text = `${typeof e.name === "string" ? e.name : ""} ${typeof e.message === "string" ? e.message : ""}`;
  } else return false;
  return STALE_SCRIPT_MESSAGES.some((m) => text.includes(m));
}

export type StampStore = { getItem(key: string): string | null; setItem(key: string, value: string): void };

export function claimReload(
  href: string,
  now: number,
  store: () => StampStore | null | undefined,
  memory: { reloaded: boolean },
): boolean {
  if (memory.reloaded) return false;
  try {
    const s = store();
    if (s) {
      const last = Number(s.getItem(STAMP_PREFIX + href));
      if (last && now - last >= 0 && now - last < RELOAD_WINDOW_MS) return false;
      s.setItem(STAMP_PREFIX + href, String(now));
    }
  } catch {
    // unreadable or unwritable: reload once anyway, the memory holds it
  }
  memory.reloaded = true;
  return true;
}

type Win = Pick<Window, "addEventListener" | "location"> & { sessionStorage?: Storage };

/** Add the two listeners. The `error` one is in the CAPTURE phase: a <script> or <link> that fails to load fires its
 *  error on the element, and that event does not bubble to window. */
export function installStaleScriptReload(win: Win = window, now: () => number = Date.now): void {
  const memory = { reloaded: false };
  const session = () => win.sessionStorage;
  const reload = () => {
    if (claimReload(win.location.href, now(), session, memory)) win.location.reload();
  };
  win.addEventListener(
    "error",
    (e: Event) => {
      const t = e.target as { tagName?: string; src?: string; href?: string } | null;
      if (t && (t as unknown) !== win && t.tagName) {
        const url = String(t.src || t.href || "");
        if ((t.tagName === "SCRIPT" || t.tagName === "LINK") && url.includes(FORM_ASSET_PREFIX)) reload();
        return;
      }
      const ev = e as ErrorEvent;
      if (isStaleScriptError(ev.error) || isStaleScriptError(ev.message)) reload();
    },
    true,
  );
  win.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    if (isStaleScriptError(e.reason)) reload();
  });
}
